import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workoutsApi } from '@/lib/api';
import { formatDate, formatWork } from '@/lib/utils';
import type { Workout } from '@/types';
import { ChevronRightIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';

export default function WorkoutsPage() {
  const [extraWorkouts, setExtraWorkouts] = useState<Workout[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['workouts'],
    queryFn: async () => {
      const res = await workoutsApi.list({ limit: 20, offset: 0 });
      if (res.success && res.data) {
        setHasMore(res.data.has_more);
        setExtraWorkouts([]); // Reset extra workouts on fresh fetch
        return res.data.items;
      }
      return [];
    },
  });

  const loadMore = async () => {
    setLoadingMore(true);
    const currentCount = (data?.length || 0) + extraWorkouts.length;
    const res = await workoutsApi.list({ limit: 20, offset: currentCount });
    if (res.success && res.data) {
      setExtraWorkouts((prev) => [...prev, ...res.data!.items]);
      setHasMore(res.data.has_more);
    }
    setLoadingMore(false);
  };

  const workouts = [...(data || []), ...extraWorkouts];

  if (isLoading && !data) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Calculate workout duration
  const getDuration = (workout: Workout) => {
    if (!workout.ended_at) return null;
    const start = new Date(workout.started_at).getTime();
    const end = new Date(workout.ended_at).getTime();
    const minutes = Math.round((end - start) / 60000);
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`;
  };

  return (
    <div className="page-container">
      {/* Empty header for consistent spacing */}
      <header className="page-header" />

      <div className="p-4 space-y-3">
        {workouts.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">Тренировок пока нет</p>
              <p className="text-sm text-gray-400 mt-1">Начните тренировку с главной страницы</p>
            </div>
          </div>
        ) : (
          <>
            {workouts.map((workout) => {
              const isActive = !workout.ended_at;
              const duration = getDuration(workout);
              
              return (
                <Link
                  key={workout.id}
                  to={`/workouts/${workout.id}`}
                  className="block card p-4 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Date and status */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {formatDate(workout.date)}
                        </span>
                        {isActive && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-50 text-primary-600 rounded">
                            В процессе
                          </span>
                        )}
                      </div>
                      
                      {/* Stats row */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {/* Volume */}
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                          </svg>
                          <span>{formatWork(workout.work_total)}</span>
                        </div>
                        
                        {/* Sets count */}
                        {workout.sets_count !== undefined && workout.sets_count > 0 && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                            </svg>
                            <span>{workout.sets_count} подх.</span>
                          </div>
                        )}
                        
                        {/* Duration */}
                        {duration && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{duration}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRightIcon className="w-5 h-5 text-gray-300" />
                  </div>
                </Link>
              );
            })}

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-secondary w-full"
              >
                {loadingMore ? <LoadingSpinner size="sm" /> : 'Загрузить ещё'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
